import type * as monaco from 'monaco-editor/esm/vs/editor/editor.api';

// Helper to convert RGB to hex (without #)
function rgbToHex(r: number, g: number, b: number): string {
  return ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}
function getCssVarColor(varName: string, fallback: string = '808080'): string {
  try {
    const cssValue = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    
    if (!cssValue) return fallback;
    
    // Normalize the color value
    let colorToConvert = cssValue;
    if (cssValue.match(/^\d+(\.\d+)?\s+\d+(\.\d+)?%?\s+\d+(\.\d+)?%?$/)) {
      // HSL values without prefix
      colorToConvert = `hsl(${cssValue})`;
    }
    
    // Use canvas to convert any CSS color to rgba
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    const ctx = canvas.getContext('2d')!;
    
    ctx.fillStyle = colorToConvert;
    ctx.fillRect(0, 0, 1, 1);
    
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return rgbToHex(r, g, b);
  } catch (e) {
    console.warn(`Failed to parse color for ${varName}:`, e);
    return fallback;
  }
}

// Add alpha channel to hex color
function withAlpha(hex: string, alpha: number): string {
  const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0');
  return hex + alphaHex;
}

export const generateMonacoThemeData = (isDark: boolean, isMobile = false): monaco.editor.IStandaloneThemeData => {
  // Theme colors with sensible fallbacks
  const foreground = getCssVarColor('--foreground', isDark ? 'e1e1e1' : '1a1a1a');
  const background = getCssVarColor('--background', isDark ? '1e1e1e' : 'ffffff');
  const primary = getCssVarColor('--primary', isDark ? '569cd6' : '0066cc');
  const secondary = getCssVarColor('--secondary', isDark ? '2d2d30' : 'f3f4f6');
  const muted = getCssVarColor('--muted', isDark ? '2d2d30' : 'f3f4f6');
  const mutedForeground = getCssVarColor('--muted-foreground', isDark ? '808080' : '6b7280');
  const accent = getCssVarColor('--accent', isDark ? '264f78' : 'e0f2fe');
  const accentForeground = getCssVarColor('--accent-foreground', isDark ? 'd7ba7d' : '0369a1');
  const destructive = getCssVarColor('--destructive', isDark ? 'f48771' : 'ef4444');
  const border = getCssVarColor('--border', isDark ? '464647' : 'e5e7eb');
  const ring = getCssVarColor('--ring', isDark ? '569cd6' : '3b82f6');
  const card = getCssVarColor('--card', isDark ? '252526' : 'ffffff');
  const popover = getCssVarColor('--popover', isDark ? '1e1e1e' : 'ffffff');
  const input = getCssVarColor('--input', isDark ? '3c3c3c' : 'ffffff');
  
  // Chart colors for syntax highlighting with fallbacks
  const chart1 = getCssVarColor('--chart-1', isDark ? '569cd6' : '0066cc'); // blues - keywords
  const chart2 = getCssVarColor('--chart-2', isDark ? 'ce9178' : 'a31515'); // oranges/reds - strings
  const chart3 = getCssVarColor('--chart-3', isDark ? '4ec9b0' : '267f99'); // cyans - types/classes
  const chart4 = getCssVarColor('--chart-4', isDark ? 'b5cea8' : '098658'); // greens - numbers
  const chart5 = getCssVarColor('--chart-5', isDark ? 'dcdcaa' : '795e26'); // yellows - functions

  // Define comprehensive syntax highlighting rules
  const rules: monaco.editor.IStandaloneThemeData['rules'] = [
    // Comments
    { token: 'comment', foreground: mutedForeground, fontStyle: 'italic' },
    { token: 'comment.doc', foreground: mutedForeground, fontStyle: 'italic' },
    { token: 'comment.block', foreground: mutedForeground, fontStyle: 'italic' },
    { token: 'comment.line', foreground: mutedForeground, fontStyle: 'italic' },
    
    // Strings
    { token: 'string', foreground: chart2 },
    { token: 'string.escape', foreground: accentForeground },
    { token: 'string.key', foreground: chart2 },
    { token: 'string.value', foreground: chart2 },
    { token: 'string.regexp', foreground: destructive },
    { token: 'string.escape.invalid', foreground: destructive },
    { token: 'string.quoted', foreground: chart2 },
    { token: 'string.template', foreground: chart2 },
    { token: 'string.invalid', foreground: destructive },
    
    // Keywords & Control flow
    { token: 'keyword', foreground: chart1 },
    { token: 'keyword.control', foreground: chart1 },
    { token: 'keyword.flow', foreground: chart1 },
    { token: 'keyword.operator', foreground: accentForeground },
    { token: 'keyword.operator.new', foreground: chart1 },
    { token: 'keyword.operator.expression', foreground: chart1 },
    { token: 'keyword.operator.logical', foreground: chart1 },
    { token: 'keyword.operator.assignment', foreground: foreground },
    { token: 'keyword.other', foreground: chart1 },
    
    // Storage & Modifiers
    { token: 'storage', foreground: chart1 },
    { token: 'storage.type', foreground: chart1 },
    { token: 'storage.modifier', foreground: chart1 },
    { token: 'storage.type.function', foreground: chart1 },
    { token: 'storage.type.class', foreground: chart1 },
    { token: 'storage.type.interface', foreground: chart1 },
    
    // Constants & Literals
    { token: 'constant', foreground: chart4 },
    { token: 'constant.language', foreground: chart1 },
    { token: 'constant.language.boolean', foreground: chart1 },
    { token: 'constant.language.null', foreground: chart1 },
    { token: 'constant.language.undefined', foreground: chart1 },
    { token: 'constant.numeric', foreground: chart4 },
    { token: 'constant.numeric.hex', foreground: chart4 },
    { token: 'constant.numeric.binary', foreground: chart4 },
    { token: 'constant.numeric.octal', foreground: chart4 },
    { token: 'constant.regexp', foreground: destructive },
    { token: 'constant.rgb-value', foreground: chart4 },
    { token: 'constant.character.escape', foreground: accentForeground },
    
    // Numbers
    { token: 'number', foreground: chart4 },
    { token: 'number.hex', foreground: chart4 },
    { token: 'number.binary', foreground: chart4 },
    { token: 'number.octal', foreground: chart4 },
    { token: 'number.float', foreground: chart4 },
    
    // Types & Classes
    { token: 'type', foreground: chart3 },
    { token: 'type.primitive', foreground: chart3 },
    { token: 'class', foreground: chart3 },
    { token: 'class.name', foreground: chart3 },
    { token: 'interface', foreground: chart3 },
    { token: 'namespace', foreground: chart3 },
    { token: 'struct', foreground: chart3 },
    { token: 'enum', foreground: chart3 },
    { token: 'typeParameter', foreground: chart3 },
    
    // Functions & Methods
    { token: 'function', foreground: chart5 },
    { token: 'function.name', foreground: chart5 },
    { token: 'method', foreground: chart5 },
    { token: 'method.name', foreground: chart5 },
    { token: 'constructor', foreground: chart5 },
    { token: 'function.call', foreground: chart5 },
    
    // Variables & Properties
    { token: 'variable', foreground: foreground },
    { token: 'variable.name', foreground: foreground },
    { token: 'variable.readonly', foreground: accentForeground },
    { token: 'variable.defaultLibrary', foreground: chart3 },
    { token: 'variable.language', foreground: chart1 },
    { token: 'variable.language.this', foreground: chart1 },
    { token: 'variable.language.super', foreground: chart1 },
    { token: 'variable.parameter', foreground: foreground },
    { token: 'property', foreground: foreground },
    { token: 'property.readonly', foreground: accentForeground },
    { token: 'parameter', foreground: foreground },
    
    // Entities & Support
    { token: 'entity', foreground: chart3 },
    { token: 'entity.name', foreground: chart3 },
    { token: 'entity.name.function', foreground: chart5 },
    { token: 'entity.name.type', foreground: chart3 },
    { token: 'entity.name.class', foreground: chart3 },
    { token: 'entity.name.namespace', foreground: chart3 },
    { token: 'entity.name.tag', foreground: chart1 },
    { token: 'entity.other.attribute-name', foreground: chart3 },
    
    // Support library functions
    { token: 'support', foreground: chart3 },
    { token: 'support.function', foreground: chart5 },
    { token: 'support.class', foreground: chart3 },
    { token: 'support.type', foreground: chart3 },
    { token: 'support.type.builtin', foreground: chart3 },
    { token: 'support.type.primitive', foreground: chart3 },
    { token: 'support.variable', foreground: foreground },
    { token: 'support.constant', foreground: chart4 },
    
    // Operators & Punctuation
    { token: 'operator', foreground: accentForeground },
    { token: 'delimiter', foreground: mutedForeground },
    { token: 'delimiter.bracket', foreground: foreground },
    { token: 'delimiter.array', foreground: foreground },
    { token: 'delimiter.parenthesis', foreground: foreground },
    { token: 'delimiter.angle', foreground: foreground },
    { token: 'punctuation', foreground: mutedForeground },
    { token: 'punctuation.definition.comment', foreground: mutedForeground },
    { token: 'punctuation.definition.string', foreground: chart2 },
    { token: 'punctuation.separator', foreground: mutedForeground },
    { token: 'punctuation.terminator', foreground: mutedForeground },
    { token: 'punctuation.accessor', foreground: mutedForeground },
    
    // Meta scopes
    { token: 'meta', foreground: foreground },
    { token: 'meta.decorator', foreground: chart5 },
    { token: 'meta.preprocessor', foreground: chart1 },
    { token: 'meta.function-call', foreground: chart5 },
    { token: 'meta.jsx', foreground: foreground },
    { token: 'meta.embedded', foreground: foreground },
    { token: 'meta.import', foreground: chart1 },
    { token: 'meta.export', foreground: chart1 },
    
    // Tags (HTML/JSX/XML)
    { token: 'tag', foreground: chart1 },
    { token: 'tag.open', foreground: chart1 },
    { token: 'tag.close', foreground: chart1 },
    { token: 'attribute', foreground: chart3 },
    { token: 'attribute.name', foreground: chart3 },
    { token: 'attribute.value', foreground: chart2 },
    
    // Markup (Markdown)
    { token: 'markup', foreground: foreground },
    { token: 'markup.heading', foreground: chart1, fontStyle: 'bold' },
    { token: 'markup.bold', fontStyle: 'bold' },
    { token: 'markup.italic', fontStyle: 'italic' },
    { token: 'markup.underline', fontStyle: 'underline' },
    { token: 'markup.strikethrough', fontStyle: 'strikethrough' },
    { token: 'markup.quote', foreground: mutedForeground, fontStyle: 'italic' },
    { token: 'markup.list', foreground: chart1 },
    { token: 'markup.link', foreground: chart2 },
    { token: 'markup.raw', foreground: chart4 },
    { token: 'markup.raw.inline', foreground: chart4 },
    
    // Annotations
    { token: 'annotation', foreground: chart5 },
    { token: 'modifier', foreground: chart1 },
    
    // Invalid & Errors
    { token: 'invalid', foreground: destructive },
    { token: 'invalid.illegal', foreground: destructive },
    { token: 'invalid.deprecated', foreground: destructive, fontStyle: 'strikethrough' },
    
    // Misc
    { token: 'emphasis', fontStyle: 'italic' },
    { token: 'strong', fontStyle: 'bold' },
    { token: 'header', foreground: chart1, fontStyle: 'bold' },
  ];

  return {
    base: isDark ? 'vs-dark' : 'vs',
    inherit: true,
    rules: rules,
    colors: {
      // Core editor colors
      'editor.background': `#${background}`,
      'editor.foreground': `#${foreground}`,
      'editorLineNumber.foreground': `#${mutedForeground}`,
      'editorLineNumber.activeForeground': `#${foreground}`,
      'editor.lineHighlightBackground': `#${withAlpha(accent, 0.1)}`,
      'editor.lineHighlightBorder': `#${withAlpha(accent, 0.2)}`,
      'editorCursor.foreground': `#${primary}`,
      'editorCursor.background': `#${background}`,
      'editor.rangeHighlightBackground': `#${withAlpha(accent, 0.15)}`,
      'editorWhitespace.foreground': `#${withAlpha(border, 0.4)}`,
      'editorIndentGuide.background': `#${withAlpha(border, 0.3)}`,
      'editorIndentGuide.activeBackground': `#${withAlpha(ring, 0.5)}`,
      'editorRuler.foreground': `#${withAlpha(border, 0.5)}`,
      'editorCodeLens.foreground': `#${mutedForeground}`,
      
      // Gutter
      'editorGutter.background': `#${background}`,
      'editorGutter.modifiedBackground': `#${chart1}`,
      'editorGutter.addedBackground': `#${chart4}`,
      'editorGutter.deletedBackground': `#${destructive}`,
      
      // Selection & Find colors
      'editor.selectionBackground': `#${withAlpha(accent, 0.3)}`,
      'editor.selectionForeground': `#${foreground}`,
      'editor.inactiveSelectionBackground': `#${withAlpha(accent, 0.15)}`,
      'editor.selectionHighlightBackground': `#${withAlpha(accent, 0.2)}`,
      'editor.selectionHighlightBorder': `#${withAlpha(accent, 0.4)}`,
      'editor.wordHighlightBackground': `#${withAlpha(accent, 0.15)}`,
      'editor.wordHighlightStrongBackground': `#${withAlpha(accent, 0.25)}`,
      'editor.wordHighlightBorder': `#${withAlpha(accent, 0.4)}`,
      'editor.wordHighlightStrongBorder': `#${withAlpha(accent, 0.5)}`,
      'editor.findMatchBackground': `#${withAlpha(chart4, 0.3)}`,
      'editor.findMatchHighlightBackground': `#${withAlpha(chart4, 0.15)}`,
      'editor.findRangeHighlightBackground': `#${withAlpha(chart4, 0.1)}`,
      'editor.findMatchBorder': `#${chart4}`,
      'editor.findMatchHighlightBorder': `#${withAlpha(chart4, 0.5)}`,
      'editor.findRangeHighlightBorder': `#${withAlpha(chart4, 0.3)}`,
      
      // Links
      'editorLink.activeForeground': `#${chart1}`,
      
      // Bracket matching
      'editorBracketMatch.background': `#${withAlpha(accent, 0.2)}`,
      'editorBracketMatch.border': `#${accent}`,
      
      // Errors, warnings, info
      'editorError.foreground': `#${destructive}`,
            'editorError.border': isMobile ? `#${withAlpha(destructive, 0.5)}` : `#${destructive}`,
      'editorWarning.foreground': `#${chart2}`,
      'editorWarning.border': `#${chart2}`,
      'editorInfo.foreground': `#${chart1}`,
      'editorInfo.border': `#${chart1}`,
      'editorHint.foreground': `#${chart3}`,
      'editorHint.border': `#${chart3}`,
      
      // Diff editor colors
      'diffEditor.insertedTextBackground': `#${withAlpha(chart4, 0.2)}`,
      'diffEditor.insertedTextBorder': `#${withAlpha(chart4, 0.4)}`,
      'diffEditor.removedTextBackground': `#${withAlpha(destructive, 0.2)}`,
      'diffEditor.removedTextBorder': `#${withAlpha(destructive, 0.4)}`,
      'diffEditor.insertedLineBackground': `#${withAlpha(chart4, 0.15)}`,
      'diffEditor.removedLineBackground': `#${withAlpha(destructive, 0.15)}`,
      'diffEditor.diagonalFill': `#${withAlpha(border, 0.3)}`,
      
      // Widget colors
      'editorWidget.background': `#${card}`,
      'editorWidget.foreground': `#${foreground}`,
      'editorWidget.border': `#${border}`,
      'editorWidget.resizeBorder': `#${border}`,
      'editorSuggestWidget.background': `#${card}`,
      'editorSuggestWidget.border': `#${border}`,
      'editorSuggestWidget.foreground': `#${foreground}`,
      'editorSuggestWidget.highlightForeground': `#${chart1}`,
      'editorSuggestWidget.selectedBackground': `#${accent}`,
      'editorSuggestWidget.selectedForeground': `#${accentForeground}`,
      'editorSuggestWidget.selectedIconForeground': `#${accentForeground}`,
      'editorSuggestWidget.focusHighlightForeground': `#${chart1}`,
      
      // Hover widget
      'editorHoverWidget.background': `#${card}`,
      'editorHoverWidget.foreground': `#${foreground}`,
      'editorHoverWidget.border': `#${border}`,
      'editorHoverWidget.statusBarBackground': `#${muted}`,
      
      // Peek view colors
      'peekView.border': `#${chart1}`,
      'peekViewEditor.background': `#${muted}`,
      'peekViewEditorGutter.background': `#${muted}`,
      'peekViewEditor.matchHighlightBackground': `#${withAlpha(chart4, 0.3)}`,
      'peekViewEditor.matchHighlightBorder': `#${chart4}`,
      'peekViewResult.background': `#${card}`,
      'peekViewResult.fileForeground': `#${foreground}`,
      'peekViewResult.lineForeground': `#${mutedForeground}`,
      'peekViewResult.matchHighlightBackground': `#${withAlpha(chart4, 0.3)}`,
      'peekViewResult.selectionBackground': `#${accent}`,
      'peekViewResult.selectionForeground': `#${accentForeground}`,
      'peekViewTitle.background': `#${secondary}`,
      'peekViewTitleDescription.foreground': `#${mutedForeground}`,
      'peekViewTitleLabel.foreground': `#${foreground}`,
      
      // Merge conflicts
      'merge.currentHeaderBackground': `#${withAlpha(chart3, 0.3)}`,
      'merge.currentContentBackground': `#${withAlpha(chart3, 0.2)}`,
      'merge.incomingHeaderBackground': `#${withAlpha(chart1, 0.3)}`,
      'merge.incomingContentBackground': `#${withAlpha(chart1, 0.2)}`,
      'merge.border': `#${border}`,
      'merge.commonContentBackground': `#${withAlpha(mutedForeground, 0.2)}`,
      'merge.commonHeaderBackground': `#${withAlpha(mutedForeground, 0.3)}`,
      
      // Overview ruler (minimap side decorations)
      'editorOverviewRuler.border': `#${border}`,
      'editorOverviewRuler.findMatchForeground': `#${chart4}`,
      'editorOverviewRuler.rangeHighlightForeground': `#${accent}`,
      'editorOverviewRuler.selectionHighlightForeground': `#${accent}`,
      'editorOverviewRuler.wordHighlightForeground': `#${accent}`,
      'editorOverviewRuler.wordHighlightStrongForeground': `#${accent}`,
      'editorOverviewRuler.modifiedForeground': `#${chart1}`,
      'editorOverviewRuler.addedForeground': `#${chart4}`,
      'editorOverviewRuler.deletedForeground': `#${destructive}`,
      'editorOverviewRuler.errorForeground': `#${destructive}`,
      'editorOverviewRuler.warningForeground': `#${chart2}`,
      'editorOverviewRuler.infoForeground': `#${chart1}`,
      'editorOverviewRuler.bracketMatchForeground': `#${accent}`,
      
      // Scrollbar
      'scrollbar.shadow': `#${withAlpha('000000', 0.25)}`,
      'scrollbarSlider.background': `#${withAlpha(border, 0.3)}`,
      'scrollbarSlider.hoverBackground': `#${withAlpha(border, 0.5)}`,
      'scrollbarSlider.activeBackground': `#${withAlpha(border, 0.7)}`,
      
      // Minimap
      'minimap.findMatchHighlight': `#${chart4}`,
      'minimap.selectionHighlight': `#${accent}`,
      'minimap.errorHighlight': `#${destructive}`,
      'minimap.warningHighlight': `#${chart2}`,
      'minimap.background': `#${background}`,
      'minimap.selectionOccurrenceHighlight': `#${accent}`,
      'minimap.foregroundOpacity': '#000000ff',
      
      // Minimap gutter
      'minimapGutter.addedBackground': `#${chart4}`,
      'minimapGutter.modifiedBackground': `#${chart1}`,
      'minimapGutter.deletedBackground': `#${destructive}`,
      
      // Editor marker navigation
      'editorMarkerNavigation.background': `#${card}`,
      'editorMarkerNavigationError.background': `#${destructive}`,
      'editorMarkerNavigationWarning.background': `#${chart2}`,
      'editorMarkerNavigationInfo.background': `#${chart1}`,
      'editorMarkerNavigationError.headerBackground': `#${withAlpha(destructive, 0.2)}`,
      'editorMarkerNavigationWarning.headerBackground': `#${withAlpha(chart2, 0.2)}`,
      'editorMarkerNavigationInfo.headerBackground': `#${withAlpha(chart1, 0.2)}`,
      
      // Basic UI colors
      'foreground': `#${foreground}`,
      'focusBorder': `#${ring}`,
      'contrastBorder': `#${border}`,
      'widget.shadow': `#${withAlpha('000000', 0.25)}`,
      
      // Input controls
      'input.background': `#${input}`,
      'input.foreground': `#${foreground}`,
      'input.border': `#${border}`,
      'input.placeholderForeground': `#${mutedForeground}`,
      'inputOption.activeBorder': `#${ring}`,
      'inputOption.activeBackground': `#${withAlpha(accent, 0.3)}`,
      'inputOption.activeForeground': `#${foreground}`,
      'inputValidation.errorBackground': `#${withAlpha(destructive, 0.2)}`,
      'inputValidation.errorBorder': `#${destructive}`,
      'inputValidation.errorForeground': `#${foreground}`,
      'inputValidation.infoBackground': `#${withAlpha(chart1, 0.2)}`,
      'inputValidation.infoBorder': `#${chart1}`,
      'inputValidation.infoForeground': `#${foreground}`,
      'inputValidation.warningBackground': `#${withAlpha(chart2, 0.2)}`,
      'inputValidation.warningBorder': `#${chart2}`,
      'inputValidation.warningForeground': `#${foreground}`,
      
      // Dropdown
      'dropdown.background': `#${input}`,
      'dropdown.foreground': `#${foreground}`,
      'dropdown.border': `#${border}`,
      'dropdown.listBackground': `#${card}`,
      
      // Lists
      'list.activeSelectionBackground': `#${accent}`,
      'list.activeSelectionForeground': `#${accentForeground}`,
      'list.inactiveSelectionBackground': `#${withAlpha(accent, 0.5)}`,
      'list.inactiveSelectionForeground': `#${foreground}`,
      'list.hoverBackground': `#${withAlpha(accent, 0.1)}`,
      'list.hoverForeground': `#${foreground}`,
      'list.focusBackground': `#${withAlpha(accent, 0.2)}`,
      'list.focusForeground': `#${foreground}`,
      'list.focusHighlightForeground': `#${chart1}`,
      'list.focusOutline': `#${ring}`,
      'list.highlightForeground': `#${chart1}`,
      'list.dropBackground': `#${withAlpha(accent, 0.2)}`,
      'list.filterMatchBackground': `#${withAlpha(chart4, 0.2)}`,
      'list.filterMatchBorder': `#${chart4}`,
    }
  };
};